import ActionButton from "../shared/ActionButton";
import { calculatePaymentforParticipant } from "../../services/paymentsService";
import { ContestWithId } from "../../interfaces/IContests";
import { Download } from "@mui/icons-material";
import { findParticipantById } from "../../utils/firestore";
import { MonetizationOn } from "@mui/icons-material";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import { useContestContext } from "../../contexts/ContestContext";
import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "../../contexts/UserContext";
import {
  Box,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

interface RowType {
  id: string;
  firstName: string;
  lastName: string;
  numberOfAppearances: number;
  paymentAmount: number;
}

export default function ApplicationPaymentsPanel() {
  const { user } = useUserContext();
  const { application } = useApplicationContext();
  const { contest } = useContestContext();

  const [rows, setRows] = useState<RowType[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    if (application && contest) {
      const participantIds = Object.keys(application.participantPresentationsMap || {});
      const participantPresentationsMap = new Map(
        Object.entries(application.participantPresentationsMap || {})
      );

      const fetchParticipantsData = async () => {
        const participantsData = await Promise.all(
          participantIds.map(async (participantId) => {
            const numberOfAppearances = participantPresentationsMap.get(participantId) || 0;
            const participant = await findParticipantById(user?.id as string, participantId);
            if (participant) {
              return {
                id: participantId,
                firstName: participant.firstName,
                lastName: participant.lastName,
                numberOfAppearances,
                paymentAmount: calculatePaymentforParticipant(
                  contest as ContestWithId,
                  numberOfAppearances
                ),
              } as RowType;
            }
            return null;
          })
        );
        const typed = participantsData.filter((r): r is RowType => r !== null);
        setRows(typed);
      };

      fetchParticipantsData();
    }
    //eslint-disable-next-line
  }, [application, contest]);

  // Sort nazwisko A-Z, imię A-Z
  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const ln = a.lastName.localeCompare(b.lastName, "pl", { sensitivity: "base" });
      if (ln !== 0) return ln;
      return a.firstName.localeCompare(b.firstName, "pl", { sensitivity: "base" });
    });
    return copy;
  }, [rows]);

  // Filtrowanie po imieniu/nazwisku
  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    const s = search.toLowerCase();
    return sortedRows.filter(
      (r) => r.firstName.toLowerCase().includes(s) || r.lastName.toLowerCase().includes(s)
    );
  }, [sortedRows, search]);

  // Paginacja
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const totalCount = filteredRows.length;
  const totalAmount = filteredRows.reduce((sum, r) => sum + (r.paymentAmount || 0), 0);

  const handleExportCsv = () => {
    const data = filteredRows;
    const header = ["Imię", "Nazwisko", "Liczba wystąpień", "Opłata startowa [PLN]"];
    const lines = [
      header.join(";"),
      ...data.map((r) =>
        [r.firstName, r.lastName, r.numberOfAppearances, r.paymentAmount]
          .map((v) => `${v}`.replace(/;/g, ","))
          .join(";")
      ),
    ];
    const csvContent = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const fileName = `Płatności - Zgłoszenie ${application?.userOrganizationName} na ${
      contest?.name || "zawody"
    }.csv`;
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 1,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{ p: 2, borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 1 }}
      >
        <MonetizationOn sx={{ color: "#6366f1" }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          Opłaty
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {/* Pasek narzędzi */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          {/* Left: Search + Export below */}
          <Stack spacing={1} sx={{ width: { xs: "100%", md: "50%" } }}>
            <TextField
              size="small"
              placeholder="Szukaj zawodnika (imię, nazwisko)..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              fullWidth
            />
          </Stack>
          <ActionButton icon={<Download />} onClick={handleExportCsv}>
            Eksport CSV
          </ActionButton>

          {/* Right: Total */}
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Zawodnik</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>Wystąpienia</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 200 }}>Kwota [PLN]</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{`${r.firstName} ${r.lastName}`}</TableCell>
                  <TableCell>{r.numberOfAppearances}</TableCell>
                  <TableCell>
                    {r.paymentAmount.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                </TableRow>
              ))}
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>Brak wyników</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" sx={{ color: "#666" }}>
          Wyświetlono {Math.min((page + 1) * rowsPerPage, totalCount)} z {totalCount}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ color: "#666" }}>
            Wierszy na stronę:
          </Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          >
            {[15, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          <ActionButton onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
            Poprzednia
          </ActionButton>
          <ActionButton
            onClick={() => setPage((p) => ((p + 1) * rowsPerPage < totalCount ? p + 1 : p))}
            disabled={(page + 1) * rowsPerPage >= totalCount}
          >
            Następna
          </ActionButton>
        </Stack>
      </Box>
    </Box>
  );
}
